/**
 * This function will return the log message based on the key
 * @param key: string - tells which log should be returned
 * @param string1
 * @param string2
 * @param string3
 * @param number1
 * @returns log message
 */
export function getLogMessage(
  key: string,
  string1?: string,
  string2?: string,
  string3?: string,
  number1?: number,
  isThirdParty = false
): string {
  // AI assessment logs use a party-specific agent label: "Vendor Assessment Agent" for third-party
  // (vendor) tasks, "AI Assessment Agent" for first-party.
  const aiAgentLabel = isThirdParty ? 'Vendor Assessment Agent' : 'AI Assessment Agent';
  const logsHash = {
    YES_ANSWER: `${string1} answered YES.`,
    NO_ANSWER: `${string1} answered NO.`,
    NA_ANSWER: `${string1} answered NA.`,
    REMOVE_ANSWER: `${string1} removed answer.`,
    PARTIAL_ANSWER: `${string1} answered PARTIAL ${number1}% on this question.`,
    SAVE_COMMENT: `${string1} added a comment.`,
    EDIT_COMMENT: `${string1} edited ${number1 > 1 ? number1 + ' comments' : 'a comment'}.`,
    DELETE_COMMENT: `${string1} deleted ${number1 > 1 ? number1 + ' comments' : 'a comment'}.`,
    SAVE_AUDITOR_COMMENT: `${string1} added a comment as an auditor.`,
    EDIT_AUDITOR_COMMENT: `${string1} edited ${number1 > 1 ? number1 + ' comments' : 'a comment'} as an auditor.`,
    DELETE_AUDITOR_COMMENT: `${string1} deleted ${number1 > 1 ? number1 + ' comments' : 'a comment'} as an auditor.`,
    MANAGER_APPROVAL: `${string1} approved the answer`,
    MANAGER_DENIAL: `${string1} denied the answer.`,
    MANAGER_EXCLUDE: `${string1} excluded this question as a manager.`,
    ARTIFACT_DOWNLOAD: `${string1} downloaded ${number1 > 1 ? number1 + ' artifacts' : 'an artifact'}.`,
    ARTIFACT_DELETE: `${string1} deleted ${number1 > 1 ? number1 + ' artifacts' : 'an artifact'}.`,
    ARTIFACT_UPLOAD: `${string1} uploaded ${number1 > 1 ? number1 + ' artifacts' : 'an artifact'}.`,
    STRONG_AUDIT: `${string1} marked this question Strong as an auditor.`,
    SATISFACTORY_AUDIT: `${string1} marked this question Satisfactory as an auditor.`,
    NEED_IMPROVEMENT_AUDIT: `${string1} required improvements on this question as an auditor.`,
    WEAK_AUDIT: `${string1} marked this question Weak as an auditor.`,
    UPDATE_AUDIT_STATUS: `${string1} updated this question from ${string2} to ${string3} as an auditor.`,
    QUESTION_TYPE: `${string1} changed question type to ${string2}.`,
    ARTIFACT_SETTINGS: `${string1} set artifact status to ${string2}.`,
    MANAGER_APPROVAL_SETTINGS: `${string1} set manager approval to ${string2}.`,
    UPDATE_VERSION_FREQUENCY: `${string1} updated version ${number1 > 1 ? 'frequencies' : 'frequency'}.`,
    ADD_TAG: `${string1} added ${number1 > 1 ? number1 + ' tags' : 'a tag'}.`,
    REMOVED_TAG: `${string1} removed ${number1 > 1 ? number1 + ' tags' : 'a tag'}.`,
    UPDATE_INTEGRATION_SETTING: `${string1} ${string2} integration for this question.`,
    UPDATE_SMART_MAPPING_SETTING: `${string1} set smart mapping ${string2} for this question.`,
    SELF_ASSIGNMENT: `${string1} self-assigned this question.`,
    ADDED_ASSIGNMENT: `${string1} assigned ${string2} on this question.`,
    ADDED_REVIEWER: `${string1} assigned ${string2} as a reviewer on this question.`,
    REMOVED_ASSIGNMENT: `${string1} removed ${string2} from this question.`,
    REMOVED_REVIEWER: `${string1} removed ${string2} as a reviewer from this question.`,
    PERMANENTLY_REMOVED_ASSIGNMENT: `${string1} permanently removed ${string2} and all their data from this question.`,
    SELF_REMOVED_ASSIGNMENT: `${string1} removed themselves from this question.`,
    SELF_PERMANENTLY_REMOVED_ASSIGNMENT: `${string1} permanently removed themselves and all their data from this question.`,
    CONFIRM_ASSESSMENT: `${string1} confirmed re-assessment on this question.`,
    UNCONFIRM_ASSESSMENT: `${string1} un-checked re-assessment on this question.`,
    REMOVE_STATUS: `${string1} removed Audit Status.`,
    UPDATE_INFO_SEC: `${string1} set Info Sec mapping ${string2} for this question.`,
    COMMENTS_SETTINGS: `${string1} set comments setting to ${string2}`,
    REMEDIATE_TASK: `This task was remediated by ${string1}`,
    REMEDIATE_TASK_OWNER: `This task was remediated by Task Owner ${string1}`,
    IGNORE_TASK_OWNER: `This task was ignored by Task Owner ${string1}`,
    SAVE_RISK_COMMENT: `${string1} added a comment.`,
    EDIT_RISK_COMMENT: `${string1} edited ${number1 > 1 ? number1 + ' comments' : 'a comment'}.`,
    DELETE_RISK_COMMENT: `${string1} deleted ${number1 > 1 ? number1 + ' comments' : 'a comment'}.`,
    ESCALATE_RISK: `${string1} escaleted this risk to ${string2} due to ${string3}`,
    DE_ESCALATE_RISK: `${string1} de-escaleted this risk`,
    ARCHIVE_RISK: `${string1} archived this risk`,
    REACTIVATE_RISK: `${string1} re-activated this risk`,
    ARCHIVE_GROUP: `${string1} archived this group`,
    REACTIVATE_GROUP: `${string1} re-activated this group`,
    DESCRIPTION_CHANGED_RISK: `${string1} updated description of group`,
    DELETE_RISK: `${string1} deleted the risk`,
    UPDATE_RISK: `${string1} updated linked risks of the group`,
    DEADLINE_DATE_SET: `${string1} updated the deadline`,
    STATUS_CHANGED_RISK: `${string1} changed the status to  ${string2}`,
    DISCRIPTION_CHANGED_RISK: `${string1} updated the description`,
    TAGS_ADDED_RISK: `${string1} added some tags to this risk`,
    ASSESTS_ADDED_RISK: `${string1} added some assets to this risk`,
    OWNER_ASSIGN_RISK: `${string1} assign owner to this risk`,
    OWNER_UN_ASSIGN_RISK: `${string1} delete owner from this risk`,
    IGNORE_TASK: `This task was ignored by ${string1}`,
    ACCEPT_TASK: `This task was accepted by ${string1}`,
    REOPEN_TASK: `This task was reopened by ${string1}`,
    UPDATE_QUESTION_SEVERITY: `${string1} changed the severity from ${string2} to ${string3}`,
    UPDATE_QUESTION_DESCRIPTION: `${string1} updated the description`,
    UPDATE_PARTIAL_REPRESENTATION: `${string1} changed partial representation to ${string2}`,
    UPDATE_POSITIVE_ANSWER: `${string1} set the positive answer to ${string2}`,
    AI_ASSESSMENT_STARTED: `${string1} started ${aiAgentLabel} ${number1} for ${string2} | ${string3}.`,
    AI_ASSESSMENT_RESTARTED: `${string1} restarted ${aiAgentLabel} ${number1} for ${string2} | ${string3}.`,
    AI_ASSESSMENT_FINALIZED: `${string1} finalized ${aiAgentLabel} ${number1} for ${string2} | ${string3}.`,
    AI_ASSESSMENT_DELETED: `${string1} deleted ${aiAgentLabel} ${number1} for ${string2} | ${string3}.`,
    FRAMEWORK_QUESTION_TYPE_UPDATED: `${string1} changed framework Question Type to ${string2}.`,
    FRAMEWORK_PARTIAL_REPRESENTATION_UPDATED: `${string1} changed framework Partial Representation to ${string2}.`,
    FRAMEWORK_INITIAL_COLLECTION_METHOD_UPDATED: `${string1} changed framework Initial Collection Method to ${string2}.`,
    FRAMEWORK_ARTIFACT_SETTING_UPDATED: `${string1} set framework Mandatory Artifact to ${string2}.`,
    FRAMEWORK_COMMENT_SETTING_UPDATED: `${string1} set framework Mandatory Comment to ${string2}.`,
    FRAMEWORK_REVIEWER_SETTING_UPDATED: `${string1} set framework Mandatory Reviewer to ${string2}.`,
    FRAMEWORK_GLOBAL_CONTROL_UPDATED: `${string1} set framework Global Control to ${string2}.`,
    FRAMEWORK_INTEGRATION_SETTING_UPDATED: `${string1} ${string2} Integration for this framework.`,
    FRAMEWORK_SMART_MAPPING_SETTING_UPDATED: `${string1} set Smart Mapping ${string2} for this framework.`,
    FRAMEWORK_DURATION_UPDATED: `${string1} updated the framework Assessment Duration to ${string2} - ${string3}.`,
    FRAMEWORK_REVIEWER_ASSIGNED: `${string1} assigned ${string2} as the framework reviewer.`,
    FRAMEWORK_REVIEWER_REMOVED: `${string1} removed ${string2} as the framework reviewer.`,
    FRAMEWORK_START_FRESH_IMMEDIATE: `${string1} started a fresh assessment for this framework.`,
    FRAMEWORK_START_FRESH_SCHEDULED: `${string1} scheduled a fresh assessment start for ${string2}.`,
    FRAMEWORK_REASSESSMENT_STARTED_IMMEDIATE: `${string1} started reassessment for this framework.`,
    FRAMEWORK_REASSESSMENT_SCHEDULED: `${string1} scheduled reassessment for ${string2}.`,
    FRAMEWORK_REASSESSMENT_COMPLETED: `${string1} completed the reassessment for this framework.`,
    FRAMEWORK_REASSESSMENT_CANCELLED: `${string1} canceled the reassessment for this framework.`,
    FRAMEWORK_IMPORTED: `${string1} imported assessment data into this framework.`,
    FRAMEWORK_ARCHIVED: `${string1} archived this framework.`,
    FRAMEWORK_DELETED: `${string1} deleted this framework.`,
  };
  return logsHash[key] ? logsHash[key] : `${string1} answered ${key}`;
}
